if (!customElements.get('contact-drawer')) {
  class ContactDrawer extends HTMLElement {
    connectedCallback() {
      this.tab = this.querySelector('.contact-drawer__tab');
      this.panel = this.querySelector('.contact-drawer__panel');
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.editor = Boolean(window.Shopify && window.Shopify.designMode);
      this.previewSelected = false;
      this.escapeHandled = false;
      this.frame = null;
      this.chatRoot = null;
      this.open = false;
      this.setOpen(false);
      this.storageKey = 'pe-contact-drawer-v1';
      this.session = { started: Date.now(), eligible: false };
      if (!this.editor) {
        try {
          const saved = JSON.parse(sessionStorage.getItem(this.storageKey));
          if (saved && Number.isFinite(saved.started)) this.session = saved;
        } catch (error) {
          // Storage can be unavailable in private browsing; use this page's state.
        }
        this.saveSession();
      }

      this.tab.addEventListener('click', () => this.setOpen(!this.open, true), options);
      this.querySelector('.contact-drawer__close').addEventListener('click', () => this.setOpen(false, true), options);
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.open) {
          this.escapeHandled = true;
          this.setOpen(false, true);
        }
      }, options);
      document.addEventListener('keyup', (event) => {
        if (event.key !== 'Escape' || !this.escapeHandled) return;
        this.escapeHandled = false;
        // The theme's global panel handler otherwise moves focus to the cart icon.
        event.stopImmediatePropagation();
      }, { ...options, capture: true });
      document.addEventListener('click', (event) => {
        if (this.open && !event.composedPath().includes(this)) this.setOpen(false);
      }, options);
      this.lastScroll = window.scrollY;
      window.addEventListener('scroll', () => {
        const current = window.scrollY;
        if (current > this.lastScroll && current >= Number(this.dataset.scrollThreshold)) this.qualify();
        this.lastScroll = current;
      }, { ...options, passive: true });

      document.addEventListener('shopify:section:select', (event) => {
        if (!this.editor || event.detail.sectionId !== this.dataset.sectionId) return;
        this.previewSelected = true;
        this.refresh();
        this.setOpen(true);
      }, options);
      document.addEventListener('shopify:section:deselect', (event) => {
        if (event.detail.sectionId !== this.dataset.sectionId) return;
        this.previewSelected = false;
        this.setOpen(false);
        this.refresh();
      }, options);

      this.observer = new MutationObserver((records) => {
        if (records.some((record) => !this.contains(record.target))) this.scheduleRefresh();
      });
      this.observer.observe(document.body, {
        subtree: true, childList: true, attributes: true,
        attributeFilter: ['class', 'style', 'hidden', 'open', 'aria-hidden', 'aria-modal', 'aria-expanded']
      });
      // Inbox attaches its shadow root asynchronously, which a body observer cannot see.
      this.chatDiscovery = window.setInterval(() => this.connectChat(), 1000);
      this.connectChat();
      this.refresh();
      if (!this.editor && !this.session.eligible) {
        this.timer = window.setTimeout(() => this.qualify(), Math.max(0, Number(this.dataset.delay) * 1000 - (Date.now() - this.session.started)));
      }
    }

    disconnectedCallback() {
      this.controller.abort();
      this.observer.disconnect();
      if (this.chatObserver) this.chatObserver.disconnect();
      clearTimeout(this.timer);
      clearInterval(this.chatDiscovery);
      cancelAnimationFrame(this.frame);
    }

    saveSession() {
      if (this.editor) return;
      try {
        sessionStorage.setItem(this.storageKey, JSON.stringify(this.session));
      } catch (error) {
        // Reveal and close still work when persistence is blocked.
      }
    }

    qualify() {
      if (this.editor || this.session.eligible) return;
      this.session.eligible = true;
      clearTimeout(this.timer);
      this.saveSession();
      this.refresh();
    }

    scheduleRefresh() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.connectChat();
        this.refresh();
      });
    }

    connectChat() {
      const root = document.querySelector('shopify-chat')?.shadowRoot;
      if (!root || root === this.chatRoot) return;
      if (this.chatObserver) this.chatObserver.disconnect();
      this.chatRoot = root;
      this.chatObserver = new MutationObserver(() => this.scheduleRefresh());
      this.chatObserver.observe(root, { subtree: true, childList: true, attributes: true, attributeFilter: ['aria-expanded', 'hidden', 'class', 'style'] });
      clearInterval(this.chatDiscovery);
      this.refresh();
    }

    visible(element) {
      return Boolean(element && !element.closest('[hidden], [aria-hidden="true"]') && element.getClientRects().length &&
        (element.checkVisibility ? element.checkVisibility({ checkOpacity: true, checkVisibilityCSS: true }) : getComputedStyle(element).visibility !== 'hidden'));
    }

    blocked() {
      // Keep theme/app selectors here: third-party markup may change independently.
      if (document.body.matches('.open-cc, .open-cc--product, .open-lightbox')) return true;
      if (document.querySelector('.side-panel.active, .mobile-toggle-wrapper[open], .mobile-toggle-wrapper.active, product-add-to-cart-sticky.sticky--visible')) return true;
      if (Array.from(document.querySelectorAll('[aria-modal="true"], dialog[open]')).some((element) => this.visible(element))) return true;
      const chat = document.querySelector('shopify-chat');
      return this.visible(chat) && (chat.hasAttribute('open') || Boolean(chat.shadowRoot?.querySelector('[aria-expanded="true"]')));
    }

    refresh() {
      const show = (this.editor ? this.previewSelected : this.session.eligible) && !this.blocked();
      if (!show) this.setOpen(false);
      this.hidden = !show;
    }

    setOpen(open, restoreFocus = false) {
      if (open && (this.hidden || this.blocked())) return;
      this.open = open;
      this.classList.toggle('is-open', open);
      this.tab.setAttribute('aria-expanded', String(open));
      this.tab.hidden = open;
      this.panel.setAttribute('aria-hidden', String(!open));
      this.panel.inert = !open;
      if (open) {
        (this.panel.querySelector('a') || this.querySelector('.contact-drawer__close')).focus({ preventScroll: true });
      } else if (restoreFocus) {
        this.tab.focus({ preventScroll: true });
      }
    }
  }

  customElements.define('contact-drawer', ContactDrawer);
}
